import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import api from '@/services/api'
import { WORKFLOW_META, WORKFLOW_ORDER } from '@/config/workflowModules'
import type { PlatformModuleId } from '@/types'
import { grantedActions } from './rbacGrants'

const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve', 'export'] as const
const ACTION_LABELS: Record<(typeof ACTIONS)[number], string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  export: 'Export',
}

const CORE_ROLE_ORDER = ['super_admin', 'admin', 'analyst', 'viewer'] as const

type RbacPayload = {
  roles: Record<string, Record<string, string[]>>
  roleLabels: Record<string, string>
  modules: string[]
  /** Need an explicit grant: "All modules" never confers them (rbac.GOVERNED_RESOURCES). */
  governed?: string[]
  governedLabels?: Record<string, string>
  actions: string[]
}

function moduleLabel(moduleId: string, governedLabels?: Record<string, string>): string {
  if (moduleId === '*') return 'All modules'
  if (governedLabels?.[moduleId]) return governedLabels[moduleId]
  const meta = WORKFLOW_META[moduleId as PlatformModuleId]
  return meta?.label ?? moduleId
}

export default function RbacSettingsTab() {
  const [payload, setPayload] = useState<RbacPayload | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>('admin')
  const [draft, setDraft] = useState<Record<string, Record<string, string[]>>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<RbacPayload>('/settings/rbac')
      setPayload(res.data)
      setDraft(structuredClone(res.data.roles))
      if (!res.data.roles[selectedRole]) {
        setSelectedRole(CORE_ROLE_ORDER.find((r) => res.data.roles[r]) ?? Object.keys(res.data.roles)[0] ?? 'admin')
      }
    } catch {
      setError('Unable to load RBAC settings.')
    } finally {
      setLoading(false)
    }
  }, [selectedRole])

  useEffect(() => {
    void load()
  }, [load])

  const editableModules = useMemo(() => {
    const ids = payload?.modules ?? WORKFLOW_ORDER
    return ['*', ...ids]
  }, [payload?.modules])
  const governedModules = useMemo(() => payload?.governed ?? [], [payload?.governed])
  const governedSet = useMemo(() => new Set(governedModules), [governedModules])

  const roleSpec = draft[selectedRole] ?? {}
  const roleOrder = useMemo(() => {
    const roleKeys = Object.keys(payload?.roles ?? {})
    const priority = new Map<string, number>(CORE_ROLE_ORDER.map((role, index) => [role, index]))
    return roleKeys.sort((left, right) => {
      const leftPriority = priority.get(left) ?? CORE_ROLE_ORDER.length
      const rightPriority = priority.get(right) ?? CORE_ROLE_ORDER.length
      return leftPriority - rightPriority || left.localeCompare(right)
    })
  }, [payload?.roles])

  const togglePermission = (moduleId: string, action: string) => {
    if (selectedRole === 'super_admin') return
    setDraft((prev) => {
      const next = structuredClone(prev)
      const spec = { ...(next[selectedRole] ?? {}) }
      // Starts from what the role holds on this module now, so ticking one
      // Database Console box grants that one action, not the whole wildcard.
      const current = new Set(grantedActions(spec, moduleId, governedSet))
      if (current.has(action)) current.delete(action)
      else current.add(action)
      spec[moduleId] = [...current]
      next[selectedRole] = spec
      return next
    })
  }

  const hasAction = (moduleId: string, action: string) =>
    grantedActions(roleSpec, moduleId, governedSet).includes(action)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.patch('/settings/rbac', { roles: draft })
      setSavedAt(new Date().toLocaleTimeString())
      await load()
    } catch {
      setError('Failed to save RBAC settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-4 text-[12px] text-stone-500">Loading permissions…</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[13px] font-medium text-stone-100">Role-Based Access Control</h3>
          <p className="text-[11px] text-stone-500 mt-1">
            Permissions are enforced on API routes. Super admin always has full access.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-stone-500">Role</span>
          <div className="relative">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="input appearance-none pr-7 text-[12px] py-1.5"
            >
              {roleOrder.map((role) => (
                <option key={role} value={role}>
                  {payload?.roleLabels?.[role] ?? role}
                </option>
              ))}
            </select>
            <ChevronDown size={11} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-500" />
          </div>
        </div>
      </div>

      {error && <div className="rounded border border-red-400/20 bg-red-400/10 px-3 py-2 text-[12px] text-red-300">{error}</div>}
      {savedAt && <div className="text-[11px] text-emerald-400">Saved at {savedAt}</div>}

      {selectedRole === 'super_admin' ? (
        <div className="rounded-lg border border-amber-400/15 bg-amber-400/[0.04] px-4 py-3 text-[12px] text-stone-300">
          Super admin has unrestricted access and cannot be limited by RBAC.
        </div>
      ) : (
        <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-x-auto">
          <table className="dense-table">
            <thead>
              <tr>
                <th className="w-44">Module</th>
                {ACTIONS.map((action) => (
                  <th key={action} className="text-center w-20">{ACTION_LABELS[action]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {editableModules.map((moduleId) => (
                <tr key={moduleId}>
                  <td className="font-medium text-stone-200">{moduleLabel(moduleId)}</td>
                  {ACTIONS.map((action) => (
                    <td key={action} className="text-center">
                      <input
                        type="checkbox"
                        aria-label={`${moduleLabel(moduleId)}: ${ACTION_LABELS[action]}`}
                        checked={hasAction(moduleId, action)}
                        onChange={() => togglePermission(moduleId, action)}
                        className="h-3.5 w-3.5 rounded border-amber-400/30 bg-black/50 accent-amber-400"
                      />
                    </td>
                  ))}
                </tr>
              ))}
              {governedModules.length > 0 && (
                <tr>
                  <td colSpan={ACTIONS.length + 1} className="pt-3 text-[10px] uppercase tracking-[0.12em] text-stone-500">
                    Granted only here: All modules does not include these
                  </td>
                </tr>
              )}
              {governedModules.map((moduleId) => {
                const label = moduleLabel(moduleId, payload?.governedLabels)
                return (
                  <tr key={moduleId}>
                    <td className="font-medium text-stone-200">{label}</td>
                    {ACTIONS.map((action) => (
                      <td key={action} className="text-center">
                        <input
                          type="checkbox"
                          aria-label={`${label}: ${ACTION_LABELS[action]}`}
                          checked={hasAction(moduleId, action)}
                          onChange={() => togglePermission(moduleId, action)}
                          className="h-3.5 w-3.5 rounded border-amber-400/30 bg-black/50 accent-amber-400"
                        />
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-1">
        <button type="button" className="btn-primary text-[12px]" disabled={saving || selectedRole === 'super_admin'} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save Permissions'}
        </button>
      </div>
    </div>
  )
}
